import type { ReactElement } from 'react';
import { MenuItemLink, useCreatePath } from 'react-admin';
import { Alert } from '@mui/material';
import { FeaturesLoading, useFeature, useFeatureState } from '../featureFlags/FeatureGate';
import { useHasPermission } from '../PermissionGate';
import { useCaretakerPreference } from '../fleet/myVehicles';
import { FEATURE_BANK_MY_FLEET, MY_FLEET_TITLE } from './bankFtl';
import { UserOfferList } from './UserOfferList';

/**
 * „Zlecenia mojej floty” (makieta FTL p. Mariusza, pkt 9.3: „Bank zleceń
 * mojej floty: zlecenia obsadzone moimi pojazdami oraz zlecenia z wariantem
 * na moim pojeździe”; Etap 1, decyzja Bartka 15.10.2026; przełącznik
 * `bankMyFleetView`).
 *
 * Ta sama tabela co bank, z innym zakresem: stały filtr „moje” (`myOrders`
 * — opiekuna API czyta z konta, `user-offers/filters.ts`), własne filtry
 * i sortowanie w magazynie react-admina (`user-offers.myFleet.listParams`),
 * niezależne od listy wszystkich zleceń. Filtr widoku, nie uprawnienie —
 * jak przełącznik „Tylko moje zlecenia” (decyzja 01.10.2026).
 *
 * Adres: `/user-offers/my-fleet` (trasa w zasobie `user-offers`, więc
 * kliknięcie wiersza prowadzi do zwykłej karty zlecenia).
 */

export const MY_FLEET_PATH = 'my-fleet';
export const MY_FLEET_STORE_KEY = 'user-offers.myFleet';
export { MY_FLEET_TITLE };

/** Wyłączony przełącznik — jak wyłączona trasa w API (komunikat jak `FEATURE_OFF_REFUSAL`). */
export const MY_FLEET_OFF_TEXT = 'Ta funkcja jest wyłączona w wersji aplikacji, z której korzystasz.';

/** Podpowiedź nad listą, gdy konto nie wskazało opiekuna — lista byłaby pusta bez powodu. */
export const MY_FLEET_NO_CARETAKER =
  'Lista pokazuje zlecenia aut, których jesteś opiekunem FTL. Wskaż siebie w polu „Jestem opiekunem FTL” ' +
  'w menu użytkownika (prawy górny róg) — bez tego lista jest pusta.';

const NoCaretakerNotice = () => {
  const { caretaker, loaded } = useCaretakerPreference();
  if (!loaded || caretaker !== null) return null;
  return (
    <Alert severity="info" sx={{ mb: 1 }}>
      {MY_FLEET_NO_CARETAKER}
    </Alert>
  );
};

export const MyFleetBank = () => {
  const on = useFeatureState(FEATURE_BANK_MY_FLEET);
  if (on === undefined) return <FeaturesLoading />;
  if (!on) return <Alert severity="info">{MY_FLEET_OFF_TEXT}</Alert>;
  return <UserOfferList myFleet notice={<NoCaretakerNotice />} />;
};

/**
 * Pozycja menu pod „Zlecenia” — z przełącznikiem i dla planowania FTL
 * („moje” to auta opiekuna FTL; traderowi API opiekunów odpowiada 403).
 */
export const MyFleetBankMenuItem = ({
  sx,
  leftIcon,
}: {
  sx?: Record<string, unknown>;
  leftIcon?: ReactElement;
}) => {
  const on = useFeature(FEATURE_BANK_MY_FLEET);
  const canPlan = useHasPermission('FTL_PLANNING');
  const createPath = useCreatePath();
  if (!on || !canPlan) return null;
  return (
    <MenuItemLink
      to={`${createPath({ resource: 'user-offers', type: 'list' })}/${MY_FLEET_PATH}`}
      primaryText={MY_FLEET_TITLE}
      leftIcon={leftIcon}
      sx={sx}
    />
  );
};
